import {
  ProfileAccessConstraintTypeEnum,
  ProfileFieldCollectionStateEnum,
  type ProfileAccessFieldType,
  type ProfileAccessType,
  type ProfileFieldKeyType,
} from '../types/profile'

type PasswordIdentifierKey = 'nickname' | 'email' | 'phonenumber' | 'external_id'

const PASSWORD_IDENTIFIER_FIELDS: PasswordIdentifierKey[] = [
  'nickname',
  'email',
  'phonenumber',
  'external_id'
]

const PASSWORD_IDENTIFIER_LABEL_KEYS: Record<PasswordIdentifierKey, string> = {
  nickname: 'home_inscription_nickname',
  email: 'home_login_email_placeholder',
  phonenumber: 'home_inscription_phone',
  external_id: 'home_inscription_external_id'
}

const getRoleAccessField = (
  access: ProfileAccessType,
  fieldKey: ProfileFieldKeyType
): ProfileAccessFieldType | undefined => access.fields.find((field) => field.key === fieldKey)

export const isRoleAccessFieldRequired = (
  access: ProfileAccessType,
  fieldKey: ProfileFieldKeyType
): boolean => getRoleAccessField(access, fieldKey)?.collectionState === ProfileFieldCollectionStateEnum.REQUIRED

export const isRoleAccessFieldEnabled = (
  access: ProfileAccessType,
  fieldKey: ProfileFieldKeyType
): boolean => getRoleAccessField(access, fieldKey)?.collectionState !== ProfileFieldCollectionStateEnum.HIDDEN

export const hasRoleAccessAtLeastOneOfConstraint = (
  access: ProfileAccessType,
  fieldKeys: ProfileFieldKeyType[]
): boolean => access.constraints.some(
  (constraint) => constraint.type === ProfileAccessConstraintTypeEnum.AT_LEAST_ONE_OF
    && fieldKeys.every((fieldKey) => constraint.fieldKeys.includes(fieldKey))
)

export const getRoleAccessEnabledIdentifierKeys = (
  access: ProfileAccessType
): PasswordIdentifierKey[] => PASSWORD_IDENTIFIER_FIELDS.filter(
  (key) => Boolean(getRoleAccessField(access, key)?.identifierEnabled)
)

export const getPasswordSignInPlaceholder = (
  access: ProfileAccessType,
  translate: (key: string) => string,
  locale?: string
): string => {
  const enabledIdentifierKeys = getRoleAccessEnabledIdentifierKeys(access)

  if (!enabledIdentifierKeys.length) {
    return translate('home_login_identifier_placeholder')
  }

  if (enabledIdentifierKeys.length === 1 && enabledIdentifierKeys[0] === 'external_id') {
    return translate('home_login_file_placeholder')
  }

  const labels = enabledIdentifierKeys.map((key) => translate(PASSWORD_IDENTIFIER_LABEL_KEYS[key]))

  if (typeof Intl !== 'undefined' && typeof Intl.ListFormat !== 'undefined') {
    return new Intl.ListFormat(locale, {
      style: 'long',
      type: 'disjunction'
    }).format(labels)
  }

  return labels.join(', ')
}
